import {
  Box,
  DropdownSelect,
  HelperField,
  ModalBody,
  ModalFoot,
  ModalHead,
  Radio,
  SelectItem,
} from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import type { Queue } from 'lib-shared-legacy-types';
import * as React from 'react';

import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { FlowManagerEditModalComponentT, FlowType } from 'fable/flowEditor';
import { AutomationNodeTypes, withQueues } from 'hocs';
import type { WithQueuesInjectedProps } from 'hocs';
import { trackFlowActionSaved } from 'utils/flowEditor/tracking';

import type {
  AutomationNodeQueuePayloadT,
  AutomationNodeQueueT,
} from '../nodeTypes';

import styles from './shared.module.scss';

type PropsT = FlowManagerEditModalComponentT<AutomationNodeQueueT> &
  WithQueuesInjectedProps;

type ValidationT = {
  queue?: string;
};

type StateT = {
  shouldTransfer: boolean;
  validation: ValidationT;
  payload: AutomationNodeQueuePayloadT;
};

class QueueModalImp extends React.Component<PropsT, StateT> {
  constructor(props: PropsT) {
    super(props);

    const { node } = props;
    const payload =
      node.nodeType === AutomationNodeTypes.QUEUE
        ? node.payload
        : {
            transferToQueueId: null,
            queueName: null,
          };

    this.state = {
      shouldTransfer: payload.transferToQueueId !== null,
      validation: {},
      payload:
        node.nodeType === AutomationNodeTypes.QUEUE ? node.payload : payload,
    };
  }

  componentDidMount() {
    const { queues } = this.props;
    return queues.getItems();
  }

  handleAnalytics(): void {
    const { node } = this.props;
    const { payload } = this.state;

    trackFlowActionSaved(!!node.isAction, {
      flowType: FlowType.FLOW_TYPE_AUTOMATION,
      nodeType: AutomationNodeTypes.QUEUE,
      settings: {
        transferToQueue: !!payload.transferToQueueId,
      },
    });
  }

  @boundMethod
  handleConfirm(): void {
    const errors = this.validate();
    if (Object.keys(errors).length > 0) {
      this.setState({ validation: errors });
      return;
    }

    this.saveNode();
  }

  saveNode() {
    const { replaceNode } = this.props;
    const { payload } = this.state;

    this.handleAnalytics();

    const nodeOptions: AutomationNodeQueueT = {
      ...this.props.node,
      nodeType: AutomationNodeTypes.QUEUE,
      isAction: false,
      isRemovable: true,
      isEditable: true,
      payload,
    };

    replaceNode(nodeOptions);
    this.closeModal();
  }

  @boundMethod
  closeModal() {
    this.props.triggerClose();
  }

  @boundMethod
  radioOnChange(evt: React.ChangeEvent<HTMLInputElement>) {
    const option = evt.target.id as string;
    const shouldTransfer = option === 'transfer';
    const { payload } = this.state;

    this.setState({
      shouldTransfer,
      payload: {
        transferToQueueId: null,
        queueName: shouldTransfer ? payload.queueName : null,
      },
    });
  }

  getQueueOptions() {
    return this.props.queues.items;
  }

  getActiveQueueId() {
    return this.state.payload.transferToQueueId;
  }

  getPagination() {
    const { queues } = this.props;
    return {
      nextEnable: queues.hasNextPage,
      onNext: queues.nextPage,
      previousEnable: queues.hasPreviousPage,
      onPrevious: queues.previousPage,
    };
  }

  @boundMethod
  onQueueSearch(searchTerm: string | undefined) {
    const { queues } = this.props;
    if (searchTerm !== undefined && searchTerm !== queues.searchTerm) {
      queues.searchTermChange(searchTerm);
    }
  }

  @boundMethod
  onQueueSelect(queue: Queue) {
    this.setState({
      shouldTransfer: true,
      validation: {},
      payload: {
        transferToQueueId: queue.id,
        queueName: queue.name,
      },
    });
  }

  validate() {
    const errors: ValidationT = {};
    const { shouldTransfer, payload } = this.state;
    if (shouldTransfer && !payload.transferToQueueId) {
      errors.queue = 'Please select a queue';
    }

    return errors;
  }

  render() {
    const { shouldTransfer, payload, validation } = this.state;
    const { node, nodeLabel } = this.props;

    const isChanged =
      payload.transferToQueueId !== node.payload?.transferToQueueId ||
      payload.queueName !== node.payload?.queueName;

    return (
      <Box width="600px">
        <ModalHead title={nodeLabel} onClose={this.closeModal}>
          Re-offer conversation to the original queue or move it to a different
          queue.
        </ModalHead>
        <ModalBody>
          <Box>
            <label className={styles.optionLabel}>
              <Radio
                id="requeue"
                name="shouldTransfer"
                checked={!shouldTransfer}
                onChange={this.radioOnChange as () => void}
              />
              Re-offer conversation to original queue
            </label>
            <label className={styles.optionLabel}>
              <Radio
                id="transfer"
                name="shouldTransfer"
                checked={shouldTransfer}
                onChange={this.radioOnChange as () => void}
              />
              Move to queue
            </label>

            <HelperField
              fieldId="queue"
              label="Select queue"
              errorMessage={validation.queue}
              required={shouldTransfer}
            >
              <></>
            </HelperField>
            <DropdownSelect
              containerProps={{ className: styles.dropdownContainer }}
              buttonProps={{
                disabled: !shouldTransfer,
              }}
              itemComponent={SelectItem}
              items={this.getQueueOptions()}
              activeItem={this.getActiveQueueId()}
              hasSearch
              pagination={this.getPagination()}
              onSearch={this.onQueueSearch}
              onSelect={this.onQueueSelect}
            >
              {payload.queueName ?? 'Select queue'}
            </DropdownSelect>
          </Box>
        </ModalBody>
        <ModalFoot
          confirmOnClick={this.handleConfirm}
          confirmName={getConfirmButtonVerb(node)}
          confirmProps={{ disabled: !isChanged }}
          cancelOnClick={this.closeModal}
        />
      </Box>
    );
  }
}

export const QueueModal = withQueues<PropsT>(QueueModalImp);
